import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NavHeader from '../components/NavHeader';
import { fetchUsers, updateUser, createUser } from '../api';

const ROLES = ['admin', 'approver', 'processor'];

function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [saving, setSaving] = useState(false);
  // Synchronous mirror of the edit fields. React state commits are tied to
  // render timing, so a fast "change a field, then click Save" can leave the
  // Save handler reading a stale value and dropping the edit ("No fields to
  // update", works on the second click). The ref is written synchronously in
  // each onChange, before any later click event fires, so handleSave always
  // sees the latest values regardless of when React re-renders.
  const editRef = useRef({ email: '', name: '', role: '' });
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('processor');
  const [adding, setAdding] = useState(false);

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchUsers();
      const data = res.data;
      setUsers(Array.isArray(data) ? data : (data?.results ?? []));
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users
      .filter((u) => {
        if (!term) return true;
        return [u.name, u.email, u.role].some((f) =>
          (f || '').toLowerCase().includes(term)
        );
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [users, searchTerm]);

  // Each edit setter writes the ref synchronously, then the state for rendering.
  const changeEditEmail = (v) => {
    editRef.current.email = v;
    setEditEmail(v);
  };
  const changeEditName = (v) => {
    editRef.current.name = v;
    setEditName(v);
  };
  const changeEditRole = (v) => {
    editRef.current.role = v;
    setEditRole(v);
  };

  const startEdit = (user) => {
    editRef.current = {
      email: user.email || '',
      name: user.name || '',
      role: user.role || '',
    };
    setEditingId(user.id);
    setEditEmail(user.email || '');
    setEditName(user.name || '');
    setEditRole(user.role || '');
  };

  const cancelEdit = () => {
    editRef.current = { email: '', name: '', role: '' };
    setEditingId(null);
    setEditEmail('');
    setEditName('');
    setEditRole('');
  };

  const handleSave = async (user) => {
    const edits = editRef.current;
    const fields = {};
    if (edits.email && edits.email !== user.email) fields.email = edits.email;
    if (edits.name && edits.name !== user.name) fields.name = edits.name;
    if (edits.role && edits.role !== user.role) fields.role = edits.role;
    if (Object.keys(fields).length === 0) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await updateUser(user.id, fields);
      showToast('User updated', 'success');
      cancelEdit();
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newEmail.trim()) {
      showToast('Name and email are required');
      return;
    }
    setAdding(true);
    try {
      await createUser({
        email: newEmail.trim(),
        name: newName.trim(),
        role: newRole,
      });
      showToast('User added', 'success');
      setNewEmail('');
      setNewName('');
      setNewRole('processor');
      loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add user');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="projects-page users-admin">
      <NavHeader />

      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.message}</div>
      )}

      <div className="projects-search">
        <input
          type="text"
          placeholder="Search users by name, email, role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <span className="search-count">
            {filtered.length} of {users.length} users
          </span>
        )}
      </div>

      <div className="users-add-card">
        <div className="users-add-title">Add a user</div>
        <div className="users-add-row">
          <input
            type="text"
            className="users-add-input name"
            placeholder="Full name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
          />
          <input
            type="email"
            className="users-add-input email"
            placeholder="name@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
          />
          <select
            className="users-add-select"
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={adding}
          >
            {adding ? 'Adding…' : '+ Add user'}
          </button>
        </div>
      </div>

      <div className="projects-table-container">
        {loading ? (
          <div className="loading-spinner">Loading users...</div>
        ) : (
          <table className="projects-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th style={{ width: 200 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isEditing = editingId === u.id;
                return (
                  <tr key={u.id}>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => changeEditName(e.target.value)}
                          className="inline-input"
                          autoFocus
                        />
                      ) : (
                        u.name
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editEmail}
                          onChange={(e) => changeEditEmail(e.target.value)}
                          className="inline-input"
                          placeholder="name@example.com"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSave(u);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                      ) : (
                        u.email
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <select
                          value={editRole}
                          onChange={(e) => changeEditRole(e.target.value)}
                          className="inline-input"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`status-pill status-${u.role}`}>
                          {u.role}
                        </span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleSave(u)}
                            disabled={saving}
                          >
                            {saving ? '...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => startEdit(u)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && filtered.length === 0 && (
          <div className="projects-empty">
            {searchTerm ? 'No matching users.' : 'No users yet.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default UsersAdmin;
